import { Chip, ChipGroup, SearchIcon, TablePager, TableSearch } from '@fukuri-admins/ui/common';
// TextInput は ui/common の barrel から export されていないため FormItems から直接 import する
import { TextInput } from '@fukuri-admins/ui/common/Form/FormItems';
import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { ContentsHeader } from '../../../components/common/ContentsHeader/index.tsx';
import { CreateFormClientTableContent } from '../../../components/pushNotification/CreateFormClientTableContent/index.tsx';
import { ClientType } from '../../../types/resources/ClientType.ts';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType.ts';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType.ts';
import { handleSorting } from '../../../utils/sortings.ts';
import styles from './styles.module.scss';

type Props = {
  fetchClientsData: ClientType[];
  setFetchClientsData: (fetchClientsData: ClientType[]) => void;
  sortParams: ActionTableSortParamsType;
  setSortParams: (sortParams: ActionTableSortParamsType) => void;
  isLoading: boolean;
  selectedClients: ClientType[];
  setSelectedClients: React.Dispatch<React.SetStateAction<ClientType[]>>;
  pagerProps: React.ComponentProps<typeof TablePager>;
  onSearch: (name: string) => void;
};

export const ClientList: React.FC<Props> = ({
  fetchClientsData,
  setFetchClientsData,
  sortParams,
  setSortParams,
  isLoading,
  selectedClients,
  setSelectedClients,
  pagerProps,
  onSearch,
}) => {
  const selectedClientIds = selectedClients.map(client => client.id);
  // 入力中の値。検索実行までは親に渡さない（1 文字ごとに再取得しないため）
  const [nameInput, setNameInput] = useState<string>('');

  // 入力だけ空にして再検索しないと、表示中の一覧と入力欄が食い違うため同時にクリアする
  const handleClearSearch = () => {
    setNameInput('');
    onSearch('');
  };

  // 全件選択チェックボックスの状態は state に持たず、「現在のページの選択状況」から毎回導出する。
  // state にすると、ページ移動・検索・個別選択のたびに同期が必要になり表示とズレる
  const getCurrentPageSelectAllState = (): true | false | 'indeterminate' => {
    const selectedCount = fetchClientsData.filter(client =>
      selectedClientIds.includes(client.id)
    ).length;

    // 0 件の判定を先に置く。順序を入れ替えると selectedCount === fetchClientsData.length が
    // 0 === 0 で成立し、一覧が空のときに ✓ を返してしまう
    if (selectedCount === 0) return false;
    if (selectedCount === fetchClientsData.length) return true;
    return 'indeterminate';
  };

  const selectAllState = getCurrentPageSelectAllState();

  const tableHeader: ActionTableColumnsType[] = [
    { label: '', key: 'checkbox', sortable: false, filterable: false },
    { label: '企業名', key: 'name', sortable: false, filterable: false },
    { label: '利用者数', key: 'user_count', sortable: false, filterable: false },
  ];

  const toggleSelectForRow = (row: ClientType) => {
    const isChecked = selectedClientIds.includes(row.id);
    if (isChecked) {
      setSelectedClients(prev => prev.filter(client => client.id !== row.id));
    } else {
      setSelectedClients(prev => [...prev, row]);
    }
  };

  // 「現在のページ」だけを対象にする。
  // 他ページで選択済みの企業は残したまま、現在ページ分だけを足す・引く
  const handleToggleSelectAll = () => {
    const currentPageIds = fetchClientsData.map(client => client.id);
    if (selectAllState === true) {
      setSelectedClients(prev => prev.filter(client => !currentPageIds.includes(client.id)));
    } else {
      setSelectedClients(prev => [
        ...prev.filter(client => !currentPageIds.includes(client.id)),
        ...fetchClientsData,
      ]);
    }
  };

  const renderCheckbox = (row: ClientType) => {
    const isChecked = selectedClientIds.includes(row.id);

    return (
      <input
        type='checkbox'
        checked={isChecked}
        onChange={e => {
          e.stopPropagation();
          toggleSelectForRow(row);
        }}
        onClick={e => e.stopPropagation()}
        aria-label='企業を選択'
      />
    );
  };

  return (
    <div className={styles.Root} data-testid='client-page'>
      <ContentsHeader title='配信対象企業' className={styles.noMargin} />
      {/* ローディング中も表示したままにする（入力内容とフォーカスを保つため）。
          取得の追い越しは index.tsx 側の世代番号で弾いている */}
      <TableSearch onSearch={() => onSearch(nameInput)} onClear={handleClearSearch}>
        {/* この画面は通知作成フォーム（CreateForm の form 要素）の中にあるため、
            Enter の暗黙送信を止めないと検索のつもりで確認モーダルが開く */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: この div 自体は操作対象ではなく、内側の input からバブルする Enter を横取りして暗黙送信を止めるだけ。フォーカスは input が持つためキーボードアクセスは損なわれない */}
        <div
          className={styles.searchInputWrapper}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch(nameInput);
            }
          }}
        >
          <TextInput
            type='text'
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onClear={handleClearSearch}
            placeholder='企業名で検索'
            startIcon={<SearchIcon size='sm' />}
          />
        </div>
      </TableSearch>
      <div className={styles.tableContainer}>
        <div>
          {/* 選択済みの企業を名前で常時表示する。
              検索・ページ移動で対象行が一覧から消えても、何を選んだか追えなくなるのを防ぐため。
              ローディング分岐の外に置き、取得中もスピナーに置き換わらないようにする。
              未選択時も ChipGroup を描画して 1 行ぶんの高さを確保し、1 件目の選択で下の表がずれないようにする。
              Chip は本来「選択」を表すコンポーネントだが、ここでは選択済みの一覧兼
              解除ボタンとして使う（クリックで解除） */}
          <div className={styles.selectedClientsContainer}>
            <div className={styles.selectedClientsHeading}>
              {selectedClients.length > 0
                ? `選択中の企業（${selectedClients.length}件）`
                : '企業が選択されていません。'}
            </div>
            <ChipGroup
              isMulti
              value={selectedClients.map(client => String(client.id))}
              // ChipGroup は選択解除後の値の配列を返す。
              // 解除された企業を引き算するのではなく、返ってきた値で絞り込む
              onChange={values => {
                const remainingIds = new Set(values);
                setSelectedClients(prev =>
                  prev.filter(client => remainingIds.has(String(client.id)))
                );
              }}
              aria-label='選択中の企業'
              className={styles.selectedClientsChips}
            >
              {selectedClients.map(client => (
                <Chip key={client.id} value={String(client.id)}>
                  {client.name}
                </Chip>
              ))}
            </ChipGroup>
          </div>
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              <CreateFormClientTableContent
                rows={fetchClientsData}
                columns={tableHeader}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    fetchClientsData,
                    setSortParams,
                    setFetchClientsData
                  )
                }
                handleToggleSelectAll={handleToggleSelectAll}
                renderCheckbox={renderCheckbox}
                toggleSelectForRow={toggleSelectForRow}
                selectAllState={selectAllState}
              />
              {/* 0 件のときはページ移動する先が無いのでページャーを出さない。
                  ローディング中はこの分岐ごとスピナーに差し替わり、ページャーが消えることで
                  取得中の多重ページ送りを防いでいる（index.tsx の取得 effect のコメント参照） */}
              {pagerProps.totalCount > 0 && <TablePager {...pagerProps} className={styles.pager} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
